import { Button, IconButton } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box, Cluster, Stack } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  CalendarLine,
  EmailLine,
  SlackExternalLogo,
} from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashProfilePage } from "@mirage/analytics/events/types/click_dash_profile_page";
import { CachedOrgChart } from "@mirage/mosaics/BaseProfilePage/OrgChart/Chart";
import { RichTextArea } from "@mirage/mosaics/BaseProfilePage/RichTextArea/RichTextArea";
import {
  CollapsibleSection,
  CollapsibleSectionTitle,
} from "@mirage/mosaics/UserProfilePage/CollapsibleSection";
import { ProfileEmployees } from "@mirage/mosaics/UserProfilePage/ProfileEmployees";
import { UserProfileConfetti } from "@mirage/mosaics/UserProfilePage/UserProfileConfetti";
import { UserProfileDetails } from "@mirage/mosaics/UserProfilePage/UserProfileDetails";
import {
  canUserEditUserProfile,
  convertPeopleRecommendationToBasicUserInfo,
  formatBirthday,
  getEditUserProfilePageHref,
  isValidEmail,
} from "@mirage/mosaics/UserProfilePage/utils";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import {
  useOrgChart,
  usePersonFromOrgChart,
} from "@mirage/service-people/hooks";
import {
  getDirectReports,
  getPeers,
} from "@mirage/service-people/service/utils";
import { openURL } from "@mirage/service-platform-actions";
import i18n, { Trans } from "@mirage/translations";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { UserInfo } from "@mirage/mosaics/UserProfilePage/types";

interface UserProfileBodyProps {
  user: UserInfo;
}

const getConfettiType = (user: UserInfo) => {
  const currentDate = new Date();
  const currentMonth = String(currentDate.getMonth() + 1).padStart(2, "0");
  const currentDay = String(currentDate.getDate()).padStart(2, "0");
  const currentDateStr = `${currentMonth}-${currentDay}`;

  if (user.is_deactivated) {
    return undefined;
  }

  if (
    user.birthday &&
    formatBirthday(user.birthday) === formatBirthday(currentDateStr)
  ) {
    return "birthday";
  }

  if (user.tenure !== null && user.tenure! > 0 && user.tenure! % 12 == 0) {
    return "anniversary";
  }

  return undefined;
};

export const UserProfileBody = ({ user }: UserProfileBodyProps) => {
  const currentAccount = useDropboxAccount();
  const canEdit = canUserEditUserProfile(currentAccount?.email, user.email);
  const confettiType = getConfettiType(user);
  const orgChart = useOrgChart();
  const person = usePersonFromOrgChart(user.uuid);

  const peers = person && orgChart ? getPeers(person, orgChart) : null;
  const directReports =
    person && orgChart ? getDirectReports(person, orgChart) : null;

  return (
    <>
      {confettiType && (
        <UserProfileConfetti
          displayHandle={user.display_handle}
          type={confettiType}
        />
      )}
      {!user.is_deactivated && <UserProfileButtonGroup user={user} />}
      <Stack gap="32">
        {!user.is_deactivated && (
          <Box as="div">
            <UserProfileDetails user={user} />
          </Box>
        )}
        {person && (
          <CachedOrgChart
            user={person}
            featureLine="people"
            orgChartViewType="reporting_line"
          />
        )}
        {directReports && directReports.length > 0 && (
          <ProfileEmployees
            pageUuid={user.uuid}
            sectionTitle={i18n.t("user_profile_reports_title")}
            employees={directReports.map(
              convertPeopleRecommendationToBasicUserInfo,
            )}
            peopleType="reports"
            alwaysShowAll={true}
          />
        )}
        {peers && peers.length > 0 && (
          <ProfileEmployees
            pageUuid={user.uuid}
            sectionTitle={i18n.t("user_profile_peer_title")}
            employees={peers.map(convertPeopleRecommendationToBasicUserInfo)}
            peopleType="peers"
            alwaysShowAll={true}
          />
        )}
        {!user.is_deactivated &&
          (canEdit || user.user_profile_settings.about_me) && (
            <AboutMeSection user={user} />
          )}
      </Stack>
    </>
  );
};

const AboutMeSection = ({ user }: { user: UserInfo }) => {
  const aboutMe = user.user_profile_settings.about_me;
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    setIsOpen(true);
  }, [user.uuid]);

  return (
    <Stack>
      <CollapsibleSectionTitle
        sectionTitle={i18n.t("user_profile_about_me_string")}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
      <CollapsibleSection isOpen={isOpen}>
        <Box marginTop="12" paddingBottom="12">
          {aboutMe ? (
            <RichTextArea theme="small-faint" value={aboutMe} />
          ) : (
            <AboutMePlaceholder user={user} />
          )}
        </Box>
      </CollapsibleSection>
    </Stack>
  );
};

const AboutMePlaceholder = ({ user }: { user: UserInfo }) => {
  const navigate = useNavigate();
  return (
    <Box as="div" display="flex" alignItems="center" style={{ gap: "4px" }}>
      <Text color="faint">
        <Trans
          i18nKey="user_profile_about_me_placeholder"
          components={{
            tag: (
              <Text
                style={{ textDecoration: "underline", cursor: "pointer" }}
                color="faint"
                onClick={() => {
                  const editUrl = getEditUserProfilePageHref(
                    user.display_handle,
                  );
                  if (editUrl) {
                    navigate(editUrl);
                  }
                }}
              />
            ),
          }}
        />
      </Text>
    </Box>
  );
};

const UserProfileButtonGroup = ({ user }: { user: UserInfo }) => {
  const { reportPapEvent } = useMirageAnalyticsContext();

  const email = user.email;
  const slackMessageLink = user.slack_message_link;
  const googleCalendarLink = user.google_calendar_link;

  const onClickEmail = useCallback(() => {
    if (!email) return;
    reportPapEvent(
      PAP_Click_DashProfilePage({
        dashActionSurface: "dash_profile_page",
        dashProfileAction: "email",
        featureLine: "people",
      }),
    );
    openURL(`mailto:${email}`);
  }, [reportPapEvent, email]);

  const onClickSlack = useCallback(() => {
    if (!slackMessageLink) return;
    reportPapEvent(
      PAP_Click_DashProfilePage({
        dashActionSurface: "dash_profile_page",
        dashProfileAction: "slack",
        featureLine: "people",
      }),
    );
    openURL(slackMessageLink);
  }, [reportPapEvent, slackMessageLink]);

  const onClickGoogleCalendar = useCallback(() => {
    if (!googleCalendarLink) return;
    reportPapEvent(
      PAP_Click_DashProfilePage({
        dashActionSurface: "dash_profile_page",
        dashProfileAction: "calendar",
        featureLine: "people",
      }),
    );
    openURL(googleCalendarLink);
  }, [reportPapEvent, googleCalendarLink]);

  // If person doesn't have a slack or google calendar link, don't show any buttons
  // since there is already another email link on the page
  const hasValidGoogleCalendarLink =
    isValidEmail(email) && !!googleCalendarLink?.length;
  if (!slackMessageLink?.length && !hasValidGoogleCalendarLink) {
    return null;
  }

  return (
    <Box marginBottom="24">
      <Cluster gap="8">
        {!!slackMessageLink?.length && (
          <Cluster.Item>
            <Button
              aria-label="Slack message"
              variant="primary"
              withIconStart={<UIIcon src={SlackExternalLogo} />}
              onClick={onClickSlack}
            >
              {i18n.t("user_profile_button_message_string")}
            </Button>
          </Cluster.Item>
        )}
        {isValidEmail(email) && (
          <Cluster.Item>
            <IconButton
              aria-label="Send email"
              variant="opacity"
              style={{ borderRadius: "var(--spacing__unit--1)" }}
              onClick={onClickEmail}
            >
              <UIIcon src={EmailLine} />
            </IconButton>
          </Cluster.Item>
        )}
        {hasValidGoogleCalendarLink && (
          <Cluster.Item>
            <IconButton
              aria-label="Create calendar event"
              variant="opacity"
              style={{ borderRadius: "var(--spacing__unit--1)" }}
              onClick={onClickGoogleCalendar}
            >
              <UIIcon src={CalendarLine} />
            </IconButton>
          </Cluster.Item>
        )}
      </Cluster>
    </Box>
  );
};
